import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  ExpandMore,
  AccountBalance as AccountBalanceIcon,
  Person,
  AttachMoney,
  Home,
  Phone,
  Business,
  DirectionsCar,
  CreditCard,
  Gavel,
  MoneyOff,
  Shield,
  TrendingUp,
  OpenInNew as OpenInNewIcon,
  Description,
} from '@mui/icons-material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { pfsAPI } from '../api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
const formatCurrency = (val) => {
  if (val === undefined || val === null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatEntityType = (t) => {
  const map = { soleProprietor: 'Sole Proprietor', partnership: 'Partnership', llc: 'LLC', corporation: 'Corporation', sCorp: 'S-Corp', cCorp: 'C-Corp' };
  return map[t] || t || '—';
};

const statusConfig = {
  draft: { label: 'Draft', color: 'default' },
  submitted: { label: 'Submitted', color: 'primary' },
  under_review: { label: 'Under Review', color: 'info' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  more_info_needed: { label: 'More Info Needed', color: 'warning' },
};

// ────────────────────────────────────────────────────────────────────────────
// Reusable display components
// ────────────────────────────────────────────────────────────────────────────
const InfoItem = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
    <Box sx={{ color: 'primary.main', mt: 0.3 }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
    </Box>
  </Box>
);

const SummaryCard = ({ title, value, color = 'primary.main', icon }) => (
  <Card variant="outlined" sx={{ flex: 1, minWidth: 160 }}>
    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && <Box sx={{ color, opacity: 0.8 }}>{icon}</Box>}
        <Typography variant="caption" color="text.secondary">{title}</Typography>
      </Box>
      <Typography variant="h5" fontWeight={700} sx={{ color, mt: 0.5 }}>{value}</Typography>
    </CardContent>
  </Card>
);

const Section = ({ icon, title, children, defaultExpanded = true }) => (
  <Grid item xs={12}>
    <Accordion defaultExpanded={defaultExpanded} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'grey.200', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'grey.50', borderRadius: '8px 8px 0 0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {icon}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>{children}</AccordionDetails>
    </Accordion>
  </Grid>
);

// ────────────────────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────────────────────
const PFS = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const [pfs, setPfs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build redirect URL for PFS Copilot external app
  const buildRedirectUrl = async () => {
    const baseUrl = import.meta.env.VITE_PFS_URL || 'https://pfs.corebusinesscapital.com';
    const url = new URL(baseUrl);
    const coreloanhubUrl = import.meta.env.VITE_CORELOANHUB_URL || 'https://coreloanhub.com';
    url.searchParams.append('returnUrl', `${coreloanhubUrl}/dashboard?pfs_submitted=true&source=pfs-copilot`);
    if (user?.email) url.searchParams.append('email', user.email);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/auth/generate-single-use-token`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) url.searchParams.append('authToken', res.data.token);
    } catch (err) {
      console.error('Failed to generate auth token:', err);
    }
    return url.toString();
  };

  const handleOpenPFSCopilot = async () => {
    try {
      const url = await buildRedirectUrl();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(import.meta.env.VITE_PFS_URL || 'https://pfs.corebusinesscapital.com', '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await pfsAPI.getAll();
        const items = res.data?.data || [];
        if (items.length > 0) setPfs(items[0]);
      } catch (err) {
        console.error('Failed to load PFS:', err);
        setError(t('pfsPage.failedToLoad', 'Failed to load your Personal Financial Statement.'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Loading ──
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={48} /></Box>
      </Container>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // ── No PFS yet ──
  if (!pfs) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
          <Box sx={{ fontSize: '4rem', mb: 2 }}>📋</Box>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>{t('pfsPage.noTitle', 'No Personal Financial Statement Found')}</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t('pfsPage.noSubtitle', 'Complete your SBA Form 413 — Personal Financial Statement using PFS Copilot. Once submitted, your information will appear here.')}
          </Typography>
          <Button variant="contained" size="large" endIcon={<OpenInNewIcon />} onClick={handleOpenPFSCopilot} sx={{ fontWeight: 600, px: 4, py: 1.5 }}>
            {t('pfsPage.startSbaForm', 'Start SBA Form 413')}
          </Button>
        </Paper>
      </Container>
    );
  }

  // ── Build data ──
  const sc = statusConfig[pfs.status] || statusConfig.draft;
  const statusLabel = t(`pfsPage.statusLabels.${pfs.status}`, sc.label);
  const totalAssets = pfs.calculatedTotalAssets ?? 0;
  const totalLiabilities = pfs.calculatedTotalLiabilities ?? 0;
  const netWorth = pfs.calculatedNetWorth ?? 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* ── Header ── */}
      <Paper
        elevation={0}
        sx={{
          p: 3, mb: 3, borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.2),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#0d2137' }}>
              <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              {t('pfsPage.pageTitle', 'SBA Form 413 — Personal Financial Statement')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {pfs.ownerFullName && t('pfsPage.preparedBy', { name: pfs.ownerFullName, defaultValue: `Prepared by ${pfs.ownerFullName}` })}
              {pfs.submittedAt && ` · ${t('pfsPage.submittedOn', { date: formatDate(pfs.submittedAt), defaultValue: `Submitted ${formatDate(pfs.submittedAt)}` })}`}
              {!pfs.submittedAt && pfs.updatedAt && ` · ${t('pfsPage.lastUpdated', { date: formatDate(pfs.updatedAt), defaultValue: `Last updated ${formatDate(pfs.updatedAt)}` })}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Chip label={statusLabel} color={sc.color} sx={{ fontWeight: 600 }} />
            {pfs.signatureStatus === 'signed' && (
              <Chip icon={<CheckCircle />} label={t('pfsPage.signed', 'Signed')} color="success" variant="outlined" size="small" />
            )}
          </Box>
        </Box>
      </Paper>

      {/* Status banners */}
      {pfs.status === 'more_info_needed' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('pfsPage.moreInfoNeeded', 'Additional information has been requested.')} {pfs.reviewNotes && t('pfsPage.reviewNote', { note: pfs.reviewNotes, defaultValue: `Note: ${pfs.reviewNotes}` })}
          <Button size="small" sx={{ ml: 2 }} endIcon={<OpenInNewIcon />} onClick={handleOpenPFSCopilot}>{t('pfsPage.updateInPFS', 'Update in PFS Copilot')}</Button>
        </Alert>
      )}
      {pfs.status === 'rejected' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('pfsPage.rejectedMsg', 'Your PFS was returned for revisions.')} {pfs.reviewNotes && t('pfsPage.rejectedReason', { reason: pfs.reviewNotes, defaultValue: `Reason: ${pfs.reviewNotes}` })}
        </Alert>
      )}

      {/* ── Net Worth Summary ── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <SummaryCard title={t('pfsPage.totalAssets', 'Total Assets')} value={formatCurrency(totalAssets)} color="success.main" icon={<TrendingUp fontSize="small" />} />
        <SummaryCard title={t('pfsPage.totalLiabilities', 'Total Liabilities')} value={formatCurrency(totalLiabilities)} color="error.main" icon={<MoneyOff fontSize="small" />} />
        <SummaryCard title={t('pfsPage.netWorth', 'Net Worth')} value={formatCurrency(netWorth)} color={netWorth >= 0 ? 'primary.main' : 'error.main'} icon={<AccountBalanceIcon fontSize="small" />} />
      </Stack>

      <Grid container spacing={3}>
        {/* ── Personal Information ── */}
        <Section icon={<Person sx={{ color: 'primary.main' }} />} title={t('pfsPage.personalInformation', 'Personal Information')}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoItem icon={<Person fontSize="small" />} label={t('pfsPage.fullName', 'Full Name')} value={pfs.ownerFullName} />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoItem icon={<Business fontSize="small" />} label={t('pfsPage.businessName', 'Business Name')} value={pfs.businessName} />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoItem icon={<Home fontSize="small" />} label={t('pfsPage.homeAddress', 'Home Address')} value={pfs.homeAddress} />
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoItem icon={<Business fontSize="small" />} label={t('pfsPage.businessAddress', 'Business Address')} value={pfs.businessAddress} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<Phone fontSize="small" />} label={t('pfsPage.businessPhone', 'Business Phone')} value={pfs.businessPhone} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<Phone fontSize="small" />} label={t('pfsPage.mobilePhone', 'Mobile Phone')} value={pfs.ownerMobilePhone} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<Business fontSize="small" />} label={t('pfsPage.entityType', 'Entity Type')} value={t(`pfsPage.entityTypes.${pfs.businessEntityType}`, formatEntityType(pfs.businessEntityType))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<Person fontSize="small" />} label={t('pfsPage.married', 'Married')} value={pfs.isMarried ? t('common.yes', 'Yes') : t('common.no', 'No')} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<Shield fontSize="small" />} label={t('pfsPage.certifiedWomenOwned', 'Certified Women-Owned')} value={pfs.isCertifiedWomenOwnedBusiness ? t('common.yes', 'Yes') : t('common.no', 'No')} />
            </Grid>
          </Grid>
        </Section>

        {/* ── Income ── */}
        <Section icon={<AttachMoney sx={{ color: 'success.main' }} />} title={t('pfsPage.annualIncome', 'Annual Income')}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<AttachMoney fontSize="small" />} label={t('pfsPage.annualSalary', 'Annual Salary')} value={formatCurrency(pfs.annualSalary)} />
            </Grid>
            {pfs.hasInvestmentIncome && (
              <Grid item xs={12} md={4}>
                <InfoItem icon={<TrendingUp fontSize="small" />} label={t('pfsPage.investmentIncome', 'Investment / Interest Income')} value={formatCurrency(pfs.annualInterestIncome)} />
              </Grid>
            )}
            {pfs.hasRentalIncome && (
              <Grid item xs={12} md={4}>
                <InfoItem icon={<Home fontSize="small" />} label={t('pfsPage.rentalIncome', 'Rental Income')} value={formatCurrency(pfs.rentalIncomeTotal)} />
              </Grid>
            )}
          </Grid>
        </Section>

        {/* ── Liquid Assets ── */}
        <Section icon={<AccountBalanceIcon sx={{ color: 'success.main' }} />} title={t('pfsPage.liquidAssets', 'Liquid Assets')}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<AccountBalanceIcon fontSize="small" />} label={t('pfsPage.checkingAccounts', 'Checking Accounts')} value={formatCurrency(pfs.checkingAccountsTotal)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<AccountBalanceIcon fontSize="small" />} label={t('pfsPage.savingsAccounts', 'Savings Accounts')} value={formatCurrency(pfs.savingsAccountsTotal)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<AccountBalanceIcon fontSize="small" />} label={t('pfsPage.retirementAccounts', 'Retirement Accounts')} value={formatCurrency(pfs.retirementAccountsTotal)} />
            </Grid>
          </Grid>
        </Section>

        {/* ── Investments & Insurance ── */}
        <Section icon={<TrendingUp sx={{ color: 'info.main' }} />} title={t('pfsPage.investmentsInsurance', 'Investments & Insurance')}>
          <Grid container spacing={3}>
            {pfs.hasLifeInsurance && (
              <>
                <Grid item xs={12} md={4}>
                  <InfoItem icon={<Shield fontSize="small" />} label={t('pfsPage.lifeInsuranceCashValue', 'Life Insurance Cash Value')} value={formatCurrency(pfs.lifeInsuranceValue)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem icon={<Shield fontSize="small" />} label={t('pfsPage.lifeInsuranceLoans', 'Life Insurance Loans')} value={formatCurrency(pfs.lifeInsuranceLoans)} />
                </Grid>
              </>
            )}
            {pfs.hasBrokerageAccount && (
              <Grid item xs={12} md={4}>
                <InfoItem icon={<TrendingUp fontSize="small" />} label={t('pfsPage.brokerageAccountValue', 'Brokerage Account Value')} value={formatCurrency(pfs.brokerageAccountValue)} />
              </Grid>
            )}
            {pfs.hasExchangeAccount && (
              <Grid item xs={12} md={4}>
                <InfoItem icon={<TrendingUp fontSize="small" />} label={t('pfsPage.exchangeAccountValue', 'Exchange Account Value')} value={formatCurrency(pfs.exchangeAccountValue)} />
              </Grid>
            )}
            {pfs.hasOtherAssets && (
              <Grid item xs={12} md={4}>
                <InfoItem icon={<AttachMoney fontSize="small" />} label={t('pfsPage.otherAssets', 'Other Assets')} value={formatCurrency(pfs.otherAssetsValue)} />
              </Grid>
            )}
            {!pfs.hasLifeInsurance && !pfs.hasBrokerageAccount && !pfs.hasExchangeAccount && !pfs.hasOtherAssets && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">{t('pfsPage.noInvestments', 'No investments or insurance reported.')}</Typography>
              </Grid>
            )}
          </Grid>
        </Section>

        {/* ── Real Estate ── */}
        {pfs.ownsRealEstate && pfs.properties?.length > 0 && (
          <Section icon={<Home sx={{ color: 'primary.main' }} />} title={`${t('pfsPage.realEstate', 'Real Estate')} (${pfs.properties.length})`}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>{t('pfsPage.address', 'Address')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('pfsPage.purchasePrice', 'Purchase Price')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('pfsPage.currentValue', 'Current Value')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('pfsPage.outstandingDebt', 'Outstanding Debt')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('debtSchedule.monthlyPayment', 'Monthly Payment')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">{t('debtSchedule.isCurrent', 'Current?')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pfs.properties.map((p, i) => (
                    <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}>
                      <TableCell>{p.address || '—'}</TableCell>
                      <TableCell align="right">{formatCurrency(p.purchasePrice)}</TableCell>
                      <TableCell align="right">{formatCurrency(p.currentValue)}</TableCell>
                      <TableCell align="right">{formatCurrency(p.outstandingDebt)}</TableCell>
                      <TableCell align="right">{formatCurrency(p.monthlyPayment)}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={p.isCurrent ? t('common.yes', 'Yes') : t('common.no', 'No')} color={p.isCurrent ? 'success' : 'error'} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        )}

        {/* ── Vehicles ── */}
        {pfs.ownsOrFinancesVehicles && pfs.vehicles?.length > 0 && (
          <Section icon={<DirectionsCar sx={{ color: 'primary.main' }} />} title={`${t('pfsPage.vehicles', 'Vehicles')} (${pfs.vehicles.length})`}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('pfsPage.estimatedValue', 'Estimated Value')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('pfsPage.outstandingBalance', 'Outstanding Balance')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('debtSchedule.monthlyPayment', 'Monthly Payment')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pfs.vehicles.map((v, i) => (
                    <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell align="right">{formatCurrency(v.estimatedValue)}</TableCell>
                      <TableCell align="right">{v.hasOutstandingDebt ? formatCurrency(v.outstandingBalance) : '—'}</TableCell>
                      <TableCell align="right">{v.hasOutstandingDebt ? formatCurrency(v.monthlyPayment) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        )}

        {/* ── Credit Cards ── */}
        {pfs.hasCreditCardDebt && pfs.creditCards?.length > 0 && (
          <Section icon={<CreditCard sx={{ color: 'warning.main' }} />} title={`${t('pfsPage.creditCards', 'Credit Cards')} (${pfs.creditCards.length})`}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>{t('pfsPage.cardName', 'Card Name')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('pfsPage.creditLimit', 'Credit Limit')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('debtSchedule.currentBalance', 'Current Balance')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">{t('debtSchedule.monthlyPayment', 'Monthly Payment')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pfs.creditCards.map((cc, i) => (
                    <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}>
                      <TableCell>{cc.name || '—'}</TableCell>
                      <TableCell align="right">{formatCurrency(cc.creditLimit)}</TableCell>
                      <TableCell align="right">{formatCurrency(cc.currentBalance)}</TableCell>
                      <TableCell align="right">{formatCurrency(cc.monthlyPayment)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        )}

        {/* ── Taxes, Judgments & Other Liabilities ── */}
        {(pfs.hasUnpaidTaxes || pfs.hasUnpaidJudgments || pfs.hasCosignedDebt || pfs.hasLegalClaims) && (
          <Section icon={<Gavel sx={{ color: 'error.main' }} />} title={t('pfsPage.taxesJudgments', 'Taxes, Judgments & Other Liabilities')}>
            <Grid container spacing={3}>
              {pfs.hasUnpaidTaxes && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>{t('pfsPage.unpaidTaxes', 'Unpaid Taxes')}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoItem icon={<MoneyOff fontSize="small" />} label={t('pfsPage.totalOwed', 'Total Owed')} value={formatCurrency(pfs.unpaidTaxesTotalOwed)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoItem icon={<AttachMoney fontSize="small" />} label={t('debtSchedule.monthlyPayment', 'Monthly Payment')} value={formatCurrency(pfs.unpaidTaxesMonthlyPayment)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoItem icon={<Description fontSize="small" />} label={t('pfsPage.paymentPlan', 'Payment Plan')} value={pfs.unpaidTaxesHasPaymentPlan ? t('common.yes', 'Yes') : t('common.no', 'No')} />
                  </Grid>
                  {pfs.unpaidTaxesAgencies?.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Agencies: </Typography>
                      {pfs.unpaidTaxesAgencies.map((a, i) => (
                        <Chip key={i} label={a} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Grid>
                  )}
                  <Grid item xs={12}><Divider /></Grid>
                </>
              )}
              {pfs.hasUnpaidJudgments && (
                <Grid item xs={12} md={4}>
                  <InfoItem icon={<Gavel fontSize="small" />} label={t('pfsPage.unpaidJudgments', 'Unpaid Judgments')} value={formatCurrency(pfs.unpaidJudgmentsTotal)} />
                </Grid>
              )}
              {pfs.hasCosignedDebt && (
                <>
                  <Grid item xs={12} md={4}>
                    <InfoItem icon={<MoneyOff fontSize="small" />} label={t('pfsPage.cosignedDebtTotal', 'Co-signed Debt Total')} value={formatCurrency(pfs.cosignedDebtTotal)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <InfoItem icon={<AttachMoney fontSize="small" />} label={t('pfsPage.cosignedMonthlyPayment', 'Co-signed Monthly Payment')} value={formatCurrency(pfs.cosignedDebtMonthly)} />
                  </Grid>
                </>
              )}
              {pfs.hasLegalClaims && (
                <Grid item xs={12} md={4}>
                  <InfoItem icon={<Gavel fontSize="small" />} label={t('pfsPage.legalClaims', 'Legal Claims')} value={formatCurrency(pfs.legalClaimsValue)} />
                </Grid>
              )}
            </Grid>
          </Section>
        )}

        {/* ── E-Signature ── */}
        <Section icon={<Description sx={{ color: 'primary.main' }} />} title={t('pfsPage.signatureSubmission', 'Signature & Submission')} defaultExpanded={false}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <InfoItem icon={<Description fontSize="small" />} label={t('pfsPage.signatureStatus', 'Signature Status')} value={
                pfs.signatureStatus === 'signed' ? t('pfsPage.signed', 'Signed') : pfs.signatureStatus === 'declined' ? t('pfsPage.declined', 'Declined') : t('pfsPage.pending', 'Pending')
              } />
            </Grid>
            {pfs.signedAt && (
              <Grid item xs={12} md={4}>
                <InfoItem icon={<CheckCircle fontSize="small" />} label={t('pfsPage.signedDate', 'Signed Date')} value={formatDate(pfs.signedAt)} />
              </Grid>
            )}
            {pfs.submittedAt && (
              <Grid item xs={12} md={4}>
                <InfoItem icon={<CheckCircle fontSize="small" />} label={t('pfsPage.submittedDate', 'Submitted Date')} value={formatDate(pfs.submittedAt)} />
              </Grid>
            )}
            {pfs.reviewedAt && (
              <Grid item xs={12} md={4}>
                <InfoItem icon={<CheckCircle fontSize="small" />} label={t('pfsPage.reviewedDate', 'Reviewed Date')} value={formatDate(pfs.reviewedAt)} />
              </Grid>
            )}
            {pfs.reviewNotes && (
              <Grid item xs={12}>
                <InfoItem icon={<Description fontSize="small" />} label={t('pfsPage.reviewNotes', 'Review Notes')} value={pfs.reviewNotes} />
              </Grid>
            )}
          </Grid>
        </Section>
      </Grid>

      {/* ── Footer Action ── */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button variant="outlined" endIcon={<OpenInNewIcon />} onClick={handleOpenPFSCopilot}>
          {pfs.status === 'draft' ? t('pfsPage.continueEditing', 'Continue Editing in PFS Copilot') : t('pfsPage.openPFSCopilot', 'Open PFS Copilot')}
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
        {t('pfsPage.footerNote', 'This information was submitted via PFS Copilot (SBA Form 413). To make changes, use the PFS Copilot application.')}
      </Typography>
    </Container>
  );
};

export default PFS;
